import { useEffect, useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import { Button } from '../../../shared/components/Button';
import { EmptyState } from '../../../shared/components/EmptyState';
import { Textarea } from '../../../shared/components/Textarea';
import { TextInput } from '../../../shared/components/TextInput';
import { useToast } from '../../../shared/components/Toast';
import { usePermissions } from '../../../shared/hooks/usePermissions';
import { FEATURES } from '../../../shared/lib/permissions';
import { maskCurrencyBRL, parseCurrencyBRL } from '../../../shared/utils/validators';
import { Collaborator, mensagemDeErro } from '../api';
import { Movement, MovementPayload } from '../api/vidaFuncional';
import { useColaboradores } from '../hooks';
import {
  useCancelarMovimentacao, useEstruturas, useLotacoes, useMovimentacoes, useRegistrarMovimentacao,
} from '../hooks/vidaFuncional';
import {
  CONTRACT_KIND_LABELS, MOVEMENT_FIELDS, MOVEMENT_STATUS_LABELS, MOVEMENT_TYPE_LABELS, WORK_MODE_OPTIONS, formatDate,
  isRetroactiveSalary, labelOf, manualMovementTypes,
} from './format';
import { PessoasModal } from './PessoasModal';

function Campo({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-gray-500">{label}</dt>
      <dd className="text-sm text-gray-900">{value || '—'}</dd>
    </div>
  );
}

/** Aba Organização (US-GP-005/006): lotação vigente, histórico e movimentações do vínculo aberto. */
export function OrganizacaoTab({ person }: { person: Collaborator }) {
  const toast = useToast();
  const { can } = usePermissions();
  const bond = person.bonds.find((b) => !b.closed) ?? person.bonds[0];
  const lotacoes = useLotacoes(bond?.id, can(FEATURES.pessoasLotacoes, 'view'));
  const movimentacoes = useMovimentacoes(bond?.id, can(FEATURES.pessoasMovimentacoes, 'view'));
  const cancelar = useCancelarMovimentacao();
  const [creating, setCreating] = useState(false);
  const [cancelling, setCancelling] = useState<Movement | null>(null);
  const [justification, setJustification] = useState('');

  if (!bond) return <EmptyState size="compact" title="Sem vínculo" />;
  const atual = lotacoes.data?.current ?? null;

  async function confirmarCancelamento() {
    if (!cancelling) return;
    try {
      await cancelar.mutateAsync({ id: cancelling.id, justification: justification.trim() });
      setCancelling(null);
      toast.success('Movimentação cancelada.');
    } catch (err) {
      toast.error(mensagemDeErro(err, 'Não foi possível cancelar a movimentação.'));
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-800">Lotação vigente — matrícula {bond.internalRegistration}</h3>
        {can(FEATURES.pessoasMovimentacoes, 'create') && !bond.closed && (
          <Button size="sm" onClick={() => setCreating(true)}><Plus className="mr-1 h-4 w-4" /> Nova movimentação</Button>
        )}
      </div>

      {atual ? (
        <dl className="grid grid-cols-2 gap-4 rounded-md bg-gray-50 p-4 md:grid-cols-4">
          <Campo label="Área" value={atual.orgUnit.name} />
          <Campo label="Cargo" value={atual.jobPosition.name} />
          <Campo label="Centro de custo" value={atual.costCenter ? `${atual.costCenter.code} — ${atual.costCenter.name}` : null} />
          <Campo label="Localidade" value={atual.location?.name} />
          <Campo label="Gestor" value={atual.manager?.name} />
          <Campo label="Regime" value={labelOf(WORK_MODE_OPTIONS, atual.workMode)} />
          <Campo label="Desde" value={formatDate(atual.validFrom)} />
        </dl>
      ) : (
        <EmptyState size="compact" title="Sem lotação" description="Registre a lotação inicial por uma movimentação." />
      )}

      {(lotacoes.data?.history.length ?? 0) > 1 && (
        <section>
          <h3 className="mb-2 text-sm font-semibold text-gray-800">Histórico de lotação</h3>
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="text-left text-xs uppercase text-gray-500">
              <tr><th className="py-2">Vigência</th><th className="py-2">Área</th><th className="py-2">Cargo</th><th className="py-2">Gestor</th></tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {[...(lotacoes.data?.history ?? [])].reverse().map((a) => (
                <tr key={a.id}>
                  <td className="py-2">{formatDate(a.validFrom)} a {a.validTo ? formatDate(a.validTo) : 'atual'}</td>
                  <td className="py-2">{a.orgUnit.name}</td>
                  <td className="py-2">{a.jobPosition.name}</td>
                  <td className="py-2">{a.manager?.name ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {can(FEATURES.pessoasMovimentacoes, 'view') && (
        <section>
          <h3 className="mb-2 text-sm font-semibold text-gray-800">Movimentações</h3>
          {(movimentacoes.data ?? []).length === 0 ? (
            <p className="text-sm text-gray-500">Nenhuma movimentação.</p>
          ) : (
            <ul className="divide-y divide-gray-100 rounded-md border border-gray-200">
              {[...(movimentacoes.data ?? [])].reverse().map((m) => (
                <li key={m.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm">
                  <div>
                    <p className="font-medium text-gray-900">
                      {m.typeLabel} · {formatDate(m.effectiveDate)} · <span className="font-normal">{MOVEMENT_STATUS_LABELS[m.status]}</span>
                    </p>
                    {m.justification && <p className="text-xs text-gray-500">{m.justification}</p>}
                    {m.lastError && <p className="text-xs text-red-600">Não aplicada: {m.lastError}</p>}
                    {m.cancelledReason && <p className="text-xs text-gray-500">Cancelada: {m.cancelledReason}</p>}
                  </div>
                  {can(FEATURES.pessoasMovimentacoes, 'edit') && (m.status === 'agendada' || m.status === 'aguardando_aprovacao') && (
                    <Button size="sm" variant="ghost" onClick={() => { setJustification(''); setCancelling(m); }}>Cancelar</Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      <MovementModal
        open={creating}
        bondId={bond.id}
        personId={person.id}
        hasAssignment={!!atual}
        onClose={() => setCreating(false)}
        onSaved={(m) => { setCreating(false); toast.success(m); }}
      />
      <PessoasModal
        open={cancelling !== null}
        title="Cancelar movimentação?"
        submitLabel="Cancelar movimentação"
        submitVariant="danger"
        canSubmit={justification.trim().length > 0}
        isSubmitting={cancelar.isPending}
        onClose={() => setCancelling(null)}
        onSubmit={confirmarCancelamento}
      >
        <Textarea label="Justificativa" value={justification} onChange={(e) => setJustification(e.target.value)} />
      </PessoasModal>
    </div>
  );
}

const hoje = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo' }).format(new Date());

function MovementModal({ open, bondId, personId, hasAssignment, onClose, onSaved }: {
  open: boolean; bondId: string; personId: string; hasAssignment: boolean; onClose: () => void; onSaved: (m: string) => void;
}) {
  const { can } = usePermissions();
  const registrar = useRegistrarMovimentacao(bondId);
  const tipos = manualMovementTypes(hasAssignment).filter(
    (t) => can(FEATURES.pessoasSalarios, 'create') || t !== 'alteracao_salarial',
  );
  const [type, setType] = useState('');
  const [form, setForm] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const spec = MOVEMENT_FIELDS[type];
  const show = (field: string) => !!spec?.fields.includes(field);
  const areas = useEstruturas('areas', open && (show('orgUnitId')));
  const cargos = useEstruturas('cargos', open && show('jobPositionId'));
  const centros = useEstruturas('centros', open && show('costCenterId'));
  const localidades = useEstruturas('localidades', open && show('locationId'));
  const gestores = useColaboradores({ limit: 200 });
  const gestorOptions = useMemo(
    () => (gestores.data?.data ?? []).filter((g) => g.id !== personId).map((g) => ({ value: g.id, label: g.socialName || g.name })),
    [gestores.data, personId],
  );

  useEffect(() => {
    if (!open) return;
    setType(tipos[0] ?? '');
    setForm({ effectiveDate: hoje() });
    setError(null);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }));
  const opts = (rows?: Array<{ id: string; code: string; name: string }>) =>
    (rows ?? []).map((r) => ({ value: r.id, label: `${r.code} — ${r.name}` }));

  async function submit() {
    if (!type) return setError('Selecione o tipo.');
    if (!form.effectiveDate) return setError('Informe a data de efeito.');
    const missing = spec.required.filter((f) => !form[f]);
    if (missing.length > 0) return setError('Preencha os campos obrigatórios.');
    const payload: MovementPayload = { type, effectiveDate: form.effectiveDate, justification: form.justification?.trim() || undefined };
    for (const f of spec.fields) {
      if (!form[f]) continue;
      (payload as unknown as Record<string, unknown>)[f] = f === 'salaryAmount' ? parseCurrencyBRL(form[f]) : form[f];
    }
    try {
      const m = await registrar.mutateAsync(payload);
      onSaved(m.status === 'efetivada' ? 'Movimentação efetivada.'
        : m.status === 'agendada' ? `Movimentação agendada para ${formatDate(m.effectiveDate)}.` : 'Movimentação enviada para aprovação.');
    } catch (err) {
      setError(mensagemDeErro(err, 'Não foi possível registrar a movimentação.'));
    }
  }

  return (
    <PessoasModal open={open} size="lg" title="Nova movimentação" submitLabel="Registrar" isSubmitting={registrar.isPending}
      submitError={error} onClose={onClose} onSubmit={submit}>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <AutocompleteSelect label="Tipo" options={tipos.map((t) => ({ value: t, label: MOVEMENT_TYPE_LABELS[t] }))}
          value={type} onChange={(v) => { setType(v); setForm((f) => ({ effectiveDate: f.effectiveDate })); }} />
        <TextInput label="Data de efeito" type="date" value={form.effectiveDate ?? ''} onChange={(e) => set('effectiveDate', e.target.value)} />
        {show('orgUnitId') && <AutocompleteSelect label="Área" options={opts(areas.data)} value={form.orgUnitId ?? ''} onChange={(v) => set('orgUnitId', v)} />}
        {show('jobPositionId') && <AutocompleteSelect label="Cargo" options={opts(cargos.data)} value={form.jobPositionId ?? ''} onChange={(v) => set('jobPositionId', v)} />}
        {show('costCenterId') && <AutocompleteSelect label="Centro de custo" options={opts(centros.data)} value={form.costCenterId ?? ''} onChange={(v) => set('costCenterId', v)} />}
        {show('locationId') && <AutocompleteSelect label="Localidade" options={(localidades.data ?? []).map((l) => ({ value: l.id, label: l.name }))} value={form.locationId ?? ''} onChange={(v) => set('locationId', v)} />}
        {show('managerUserId') && <AutocompleteSelect label="Gestor" options={gestorOptions} value={form.managerUserId ?? ''} onChange={(v) => set('managerUserId', v)} />}
        {show('workMode') && <AutocompleteSelect label="Regime de trabalho" options={WORK_MODE_OPTIONS} value={form.workMode ?? ''} onChange={(v) => set('workMode', v)} />}
        {show('salaryAmount') && can(FEATURES.pessoasSalarios, 'create') && (
          <TextInput label="Novo salário" value={form.salaryAmount ?? ''} onChange={(e) => set('salaryAmount', maskCurrencyBRL(e.target.value))} />
        )}
        {show('contractKind') && (
          <AutocompleteSelect label="Tipo de contrato" options={Object.entries(CONTRACT_KIND_LABELS).map(([v, l]) => ({ value: v, label: l }))}
            value={form.contractKind ?? ''} onChange={(v) => set('contractKind', v)} />
        )}
        {show('contractEndDate') && form.contractKind === 'determinado' && (
          <TextInput label="Data final prevista" type="date" value={form.contractEndDate ?? ''} onChange={(e) => set('contractEndDate', e.target.value)} />
        )}
        {show('probationEndDate') && form.contractKind === 'experiencia' && (
          <>
            <TextInput label="Fim da experiência" type="date" value={form.probationEndDate ?? ''} onChange={(e) => set('probationEndDate', e.target.value)} />
            <TextInput label="Fim da prorrogação" type="date" value={form.probationExtensionEndDate ?? ''} onChange={(e) => set('probationExtensionEndDate', e.target.value)} />
          </>
        )}
      </div>
      {form.salaryAmount && isRetroactiveSalary(type, form.effectiveDate ?? '', hoje()) && (
        <p className="text-xs text-amber-700">Salário com efeito em mês anterior: a movimentação vai para aprovação.</p>
      )}
      {spec?.fields.includes('salaryAmount') && <p className="text-xs text-gray-500">Salário vale a partir do dia 1 do mês.</p>}
      <Textarea label="Justificativa" value={form.justification ?? ''} onChange={(e) => set('justification', e.target.value)} />
    </PessoasModal>
  );
}
